<template>
  <model-form
    ref="modelForm"
    :model="globalWidgetBlock"
    submodel
    modal-type="modal"
  >
    <q-card-section class="no-padding">
      <div class="row q-gutter-x-md items-start">
        <model-form-input
          class="col"
          field="name"
          value-type="String"
          label="Name"
          :rules="[val => !$_.isEmpty(val) || 'Field is required']"
        />
        <model-form-select
          field="config.width"
          value-type="String"
          class="col"
          label="Width"
          :options="widthOptions"
          map-options
          emit-value
          :rules="[val => !$_.isEmpty(val) || 'Field is required']"
        >
          <template #option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section>
                <div class="row items-center justify-between q-gutter-x-sm">
                  <div
                    style="max-width: 25px;"
                    class="width-option col-auto"
                  >
                    <div
                      class="width-option-content"
                    >
                      <svg viewBox="0 0 220 220">
                        <use :xlink:href="'/grids.svg#' + scope.opt.value" />
                      </svg>
                    </div>
                  </div>
                  <span> {{ scope.opt.label }}</span>
                </div>
              </q-item-section>
            </q-item>
          </template>
        </model-form-select>
      </div>
      <div class="row q-gutter-x-md items-start">
        <model-form-select
          class="col"
          :field="`config.displayItems`"
          value-type="Array"
          label="Display Items"
          :options="displayItemsOptions"
          map-options
          emit-value
          multiple
          use-chips
        />
        <q-btn
          class="col-auto"
          flat
          outline
          :color="globalWidgetBlock.config.displayItems.length > 1 ? 'responsive' : 'responsive-grey'"
          :disable="globalWidgetBlock.config.displayItems.length <= 1"
          @click="$refs.displayItemsOrderDialog.show()"
        >
          <div class="text-left">
            Set <br> Order
          </div>
        </q-btn>
      </div>
      <div
        v-if="globalWidgetBlock.config.displayItems.includes('Navigation Links')"
        class="row q-gutter-x-md items-start"
      >
        <model-form-select
          class="col"
          field="navigationIds"
          value-type="Array"
          label="Navigations"
          :options="$_.unionBy(globalWidgetBlock.navigations.data, navigations.data, 'id')"
          option-label="title"
          option-value="id"
          map-options
          emit-value
          use-input
          input-style="search"
          input-debounce="300"
          multiple
          use-chips
          bottom-slots
          clearable
          :rules="[val => !$_.isEmpty(val) || 'Field is required']"
          @filter="filterNavigations"
          @clear="globalWidgetBlock.navigationIds = []"
        />
      </div>
      <div
        v-if="globalWidgetBlock.config.displayItems.includes('Social Icons')"
        class="row q-gutter-x-md items-start"
      >
        <model-form-select
          class="col"
          :field="`config.socialIconsColor`"
          value-type="String"
          label="Social Icons Color"
          :options="socialIconsColorOptions"
          map-options
          emit-value
        />
      </div>
      <div
        v-if="globalWidgetBlock.config.displayItems.includes('Market Signup Form')"
      >
        <div class="row q-gutter-x-md items-start q-my-md">
          <model-form-checkbox
            :field="`config.collectName`"
            value-type="Boolean"
            class="col"
            label="Collect Name"
          />
          <model-form-checkbox
            :field="`config.collectConsent`"
            value-type="Boolean"
            class="col"
            label="Collect Consent"
          />
        </div>
        <form-control-style-editor
          class="row q-col-gutter-md q-my-md"
          :field-type="globalWidgetBlock.config.textFieldType"
          :field-outlined="globalWidgetBlock.config.outlineTextField"
          is-text-field
          @update:model-value="setTextFieldStyle"
        />
      </div>
      <div
        v-if="globalWidgetBlock.config.displayItems.includes('Custom Image')"
        class="row q-gutter-x-md items-start q-my-md"
      >
        <div class="col">
          <model-form-image-uploader
            :field="`config.image`"
            value-type="String"
            class="col"
            min-height="300px"
            :image-url="globalWidgetBlock.imageUrl"
            :rules="[val => !$_.isEmpty(val) || 'Field is required']"
          />
        </div>
        <div class="col">
          <model-form-checkbox
            :field="`config.fillContainer`"
            value-type="Boolean"
            label="Fill Container"
          />
        </div>
      </div>
      <div
        v-if="globalWidgetBlock.config.displayItems.includes('Custom Text')"
        class="row items-start"
      >
        <model-form-select
          :field="`config.fontSize`"
          class="col-12"
          label="Font Size"
          value-type="String"
          :options="fontSizeOptions"
          map-options
          emit-value
        />
        <model-form-input
          :field="`config.text`"
          class="col-12"
          value-type="String"
          type="textarea"
          label="Custom Text"
          :rules="[val => !!val || 'Field is required']"
        />
      </div>
      <set-display-order-dialog
        ref="displayItemsOrderDialog"
        :list="globalWidgetBlock.config.displayItems"
        @reorder="handleReorder"
      />
    </q-card-section>
  </model-form>
</template>

<script>
import { DISPLAY_ITEMS, fontSizeOptions, socialIconsColorOptions, WIDTH_OPTIONS } from 'src/models/global-widget/GlobalWidgetBlock'
import FormControlStyleEditor from 'src/modules/admin/components/FormControlStyleEditor'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import { defineAsyncComponent } from 'vue'

export default {
  name: 'GlobalWidgetBlockForm',
  components: {
    ModelForm,
    FormControlStyleEditor,
    SetDisplayOrderDialog: defineAsyncComponent(() => import('src/modules/admin/components/SetDisplayOrderDialog'))
  },
  data () {
    return {
      navigationSearchString: null
    }
  },
  models: {
    globalWidgetBlock: {
      navigationIds: true,
      navigations: {
        page: {
          fields: ['id', 'slug']
        }
      },
      receiveLibraryUpdates: true,
      libraryParentId: true
    },
    navigations: {
      variables () {
        return {
          searchString: this.navigationSearchString,
          limit: 10
        }
      }
    }
  },
  created () {
    _.extend(this, {
      widthOptions: WIDTH_OPTIONS.OPTIONS,
      displayItemsOptions: this.$mh.asOptions(DISPLAY_ITEMS.ALL),
      fontSizeOptions,
      socialIconsColorOptions
    })
  },
  methods: {
    filterNavigations (val, update, abort) {
      update(() => {
        this.navigationSearchString = _.isEmpty(val) ? null : val.trim()
      })
    },
    handleReorder ({ reorderedList }) {
      this.globalWidgetBlock.config.displayItems = reorderedList
    },
    setTextFieldStyle (fieldType, fieldOutlined) {
      this.globalWidgetBlock.config.textFieldType = fieldType
      this.globalWidgetBlock.config.outlineTextField = fieldOutlined
    }
  }
}
</script>

<style lang="scss" scoped>
  .width-option {
    flex-grow: 1;
    flex-shrink: 1;
    width: 20px;
    fill-opacity: 0;
  }
  .width-option-content {
    width: 100%;
    position: relative;
  }
</style>
